import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, ShieldAlert, Loader2, Copy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const groupHex = (hex) => (hex || '').match(/.{1,8}/g)?.join(' ') || '';

const VerifySignaturePage = () => {
  const { reportId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/verify-signature/${reportId}`);
        if (alive) setState({ loading: false, data, error: null });
      } catch (err) {
        if (alive) setState({
          loading: false, data: null,
          error: err.response?.data?.detail || 'Erro ao verificar assinatura',
        });
      }
    })();
    return () => { alive = false; };
  }, [reportId]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado');
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center py-12 px-4">
      <div className="w-full max-w-xl">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-slate-700 text-lg font-semibold">
            <ShieldAlert className="w-5 h-5 text-indigo-700" />
            CogniEtica — Verificação de Integridade
          </div>
          <p className="text-sm text-slate-500 mt-1">Assinatura criptográfica SHA-256 de encerramento de denúncia.</p>
        </div>

        <Card data-testid="verify-signature-page">
          {state.loading ? (
            <CardContent className="py-12 flex items-center justify-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mr-3" />
              A verificar…
            </CardContent>
          ) : state.error ? (
            <CardContent className="py-10 text-center space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <div className="text-red-700 font-medium">{state.error}</div>
              <div className="text-sm text-slate-500">Referência: <code>{reportId}</code></div>
            </CardContent>
          ) : state.data.valid ? (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-700" data-testid="verify-valid">
                  <CheckCircle className="w-6 h-6" />
                  Assinatura válida
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  A mensagem final de encerramento desta denúncia <strong>não foi alterada</strong> desde
                  que foi assinada digitalmente pelo gestor.
                </div>

                <dl className="grid grid-cols-3 gap-y-2 text-sm">
                  <dt className="text-slate-500">Código</dt>
                  <dd className="col-span-2 font-medium text-slate-800" data-testid="verify-tracking">
                    {state.data.tracking_code}
                  </dd>

                  <dt className="text-slate-500">Estado</dt>
                  <dd className="col-span-2">
                    <Badge className="bg-emerald-100 text-emerald-700">{state.data.status}</Badge>
                  </dd>

                  <dt className="text-slate-500">Data de fecho</dt>
                  <dd className="col-span-2 font-mono text-xs text-slate-700">
                    {new Date(state.data.closed_at).toLocaleString('pt-PT')}
                  </dd>

                  <dt className="text-slate-500">Algoritmo</dt>
                  <dd className="col-span-2 font-medium text-slate-800">{state.data.algorithm}</dd>

                  <dt className="text-slate-500">Assinatura</dt>
                  <dd className="col-span-2">
                    <div className="flex items-start gap-2">
                      <code className="font-mono text-xs text-slate-700 break-all leading-relaxed">
                        {groupHex(state.data.signature)}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copy(state.data.signature)}
                        title="Copiar assinatura"
                        data-testid="copy-sig-btn"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </dd>

                  <dt className="text-slate-500">Verificado em</dt>
                  <dd className="col-span-2 text-xs text-slate-500">
                    {new Date(state.data.verified_at).toLocaleString('pt-PT')}
                  </dd>
                </dl>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700" data-testid="verify-invalid">
                  <XCircle className="w-6 h-6" />
                  Assinatura inválida
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                  {state.data.reason === 'not_closed_or_unsigned'
                    ? 'Esta denúncia não está encerrada ou não tem assinatura registada.'
                    : 'O hash recalculado não coincide com a assinatura registada. O conteúdo pode ter sido alterado.'}
                </div>
                <div className="text-sm text-slate-500">
                  Referência: <code>{reportId}</code>
                </div>
              </CardContent>
            </>
          )}
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700">← Voltar</Link>
        </div>
      </div>
    </div>
  );
};

export default VerifySignaturePage;
